import React, { useRef, useLayoutEffect, useState } from 'react';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  onClose: () => void;
  position?: { x: number; y: number };
}

const EMOJIS = ['😘', '❤️', '👍', '😂', '😢', '😮', '😭', '😜', '👏', '😍', '🙏', '🫣'];

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, onClose, position }) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    if (position && pickerRef.current) {
      // Measure picker after initial render
      const measureAndAdjust = () => {
        if (!pickerRef.current) return;
        
        const picker = pickerRef.current;
        const pickerRect = picker.getBoundingClientRect();
        const pickerWidth = pickerRect.width || 200; // Fallback estimate (6 cols × ~33px + padding)
        const pickerHeight = pickerRect.height || 120; // Fallback estimate (2 rows × ~50px + padding)
        const viewportWidth = window.innerWidth;
        const padding = 8;
        
        // Calculate centered position
        let left = position.x - pickerWidth / 2;
        let top = position.y - pickerHeight;
        
        // Clamp to viewport bounds - ensure left side doesn't go off-screen
        if (left < padding) {
          left = padding;
        } else if (left + pickerWidth > viewportWidth - padding) {
          left = viewportWidth - pickerWidth - padding;
        }
        
        // Ensure top doesn't go off-screen
        if (top < padding) {
          top = position.y + 40; // Show below button if not enough space above
        }
        
        setStyle({
          left: `${left}px`,
          top: `${top}px`,
        });
      };
      
      // Initial positioning (centered, may go off-screen temporarily)
      setStyle({
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%)',
      });
      
      // Measure and adjust after render
      requestAnimationFrame(() => {
        requestAnimationFrame(measureAndAdjust);
      });
    }
  }, [position]);

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji);
    // Don't close automatically - allow multiple reactions
    // User can close by clicking outside or using close button
  };

  return (
    <>
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      <div 
        ref={pickerRef}
        className="fixed z-50 bg-[#1E2A32] rounded-lg shadow-xl p-3 border border-gray-700"
        style={position ? style : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-700">
          <span className="text-xs text-gray-400">Add reaction</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-lg leading-none px-1"
            title="Close"
          >
            ×
          </button>
        </div>
        <div className="grid grid-cols-6 gap-2">
          {EMOJIS.map((emoji, index) => (
            <button
              key={index}
              onClick={() => handleEmojiClick(emoji)}
              className="text-2xl hover:bg-gray-700 rounded p-2 transition-colors active:scale-90"
              title={`Add ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default EmojiPicker;
